import React, { useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { useInfiniteCompanyRolesPersonnelQuery } from 'components/hooks/companyRolesPersonnelQueries';
import useWindowStorage from 'components/hooks/useWindowStorage';

import Tabs from 'components/application/Tabs';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import RolesSidePanelCoursesTab from 'components/roles/RolesSidePanelCoursesTab';
import RolesSidePanelPersonnelTab from 'components/roles/RolesSidePanelPersonnelTab';

import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function RolesShow({
  assignableCourses,
  domainCourseCompanyRoles,
  isSidePanelOpen,
  roleId,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'roles.RolesShow',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const [getInitialActiveTab, setInitialActiveTab] = useWindowStorage(
    'rolesSidePanelInitialTab',
    { store: window.sessionStorage },
  );

  const [activeTab, setActiveTab] = useState(
    getInitialActiveTab() || 'personnel',
  );

  const [loadMoreRef, loadMoreInView] = useInView();

  const prevIsSidePanelOpen = useRef(false);

  const {
    data: domainPersonnel,
    fetchNextPage: fetchNextDomainPersonnelPage,
    hasNextPage: hasNextDomainPersonnelPage,
    isFetchingNextPage: isFetchingNextDomainPersonnelPage,
    isLoading: isDomainPersonnelLoading,
  } = useInfiniteCompanyRolesPersonnelQuery({
    companyRoleId: roleId,
    queryOptions: { enabled: isSidePanelOpen },
  });

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setInitialActiveTab(tab);
  };

  (function resetOnPanelOpen() {
    if (!prevIsSidePanelOpen.current && isSidePanelOpen) {
      setActiveTab('personnel');
    }

    prevIsSidePanelOpen.current = isSidePanelOpen;
  })();

  (function fetchMorePersonnel() {
    if (
      loadMoreInView &&
      hasNextDomainPersonnelPage &&
      !isFetchingNextDomainPersonnelPage
    ) {
      fetchNextDomainPersonnelPage();
    }
  })();

  const isLoadMorePersonnelVisible =
    isDomainPersonnelLoading || !!hasNextDomainPersonnelPage;

  const courseCompanyRoles = domainCourseCompanyRoles?.data || [];

  const isCourseTabDisabled = !roleId || courseCompanyRoles.length === 0;

  // todo tabs as HOC to tack on labels to avoid having wrapper component?

  // get course name for selected courseCompanyRoles
  let courseCollection = [];
  if (assignableCourses.length > 0) {
    courseCollection = courseCompanyRoles.map((courseCompanyRole) => {
      const course = assignableCourses.find(
        (assignableCourse) =>
          assignableCourse.id ===
          courseCompanyRole.relationships.course.data.id,
      );

      return {
        courseName: course.attributes.name,
        required: courseCompanyRole.attributes.required,
      };
    });
  }

  return (
    <div className='popup__body-show tw-p-0'>
      {currentActor.isAllowedFeature('training_register') ?
        <div className='popup__body-content tw-p-0'>
          <Tabs
            activeTab={activeTab}
            modifiers={['panelled']}
            navClassName='tw-bg-grey-050'
            onTabClick={handleTabClick}
          >
            <RolesSidePanelPersonnelTab
              domainPersonnel={domainPersonnel}
              id='personnel'
              isLoadMorePersonnelVisible={isLoadMorePersonnelVisible}
              key='personnelTab'
              label={i18nTCommon('shared.personnel')}
              loadMoreRef={loadMoreRef}
            />
            <RolesSidePanelCoursesTab
              courseCollection={courseCollection}
              disabled={isCourseTabDisabled}
              id='courses'
              key='coursesTab'
              label={i18nTCommon('shared.courses')}
              tooltipText={
                isCourseTabDisabled && i18nT('coursesTab.disabledTooltip')
              }
            />
          </Tabs>
        </div>
      : <div className='popup__body-content tw-pb-0'>
          <h3 className='popup__body--sub tw-text-l tw-font-semibold'>
            {i18nT('title')}
          </h3>
          {domainPersonnel?.data.length === 0 ?
            <span>{i18nTCommon('personnel.noPersonnelHaveThisRole')}</span>
          : <ul className='popup__body--ul'>
              {domainPersonnel?.data.map((person) => (
                <li className='popup__body--li' key={person.id}>
                  {personDisplayName({ attributes: person.attributes })}
                </li>
              ))}
              {isLoadMorePersonnelVisible && (
                <li className='popup__body--li' ref={loadMoreRef}>
                  {i18nTCommon('personnel.loadingPersonnel')}
                </li>
              )}
            </ul>
          }
        </div>
      }
    </div>
  );
}

RolesShow.propTypes = {
  assignableCourses: PropTypes.arrayOf(resourceShape('course')).isRequired,
  domainCourseCompanyRoles: PropTypes.shape({
    data: PropTypes.arrayOf(resourceShape('courseCompanyRole')).isRequired,
  }),
  isSidePanelOpen: PropTypes.bool,
  roleId: PropTypes.string.isRequired,
};
